<template>
  <el-dialog title="销售订单行追溯" :visible.sync="visibleInner" width="900px" append-to-body @close="handleClose">
    <div v-if="head" class="trace-head">
      <span>{{ head.orderCode }}</span>
      <span class="sep">|</span>
      <span>{{ head.materialCode }} {{ head.materialName }}</span>
      <span class="sep">|</span>
      <span>订单量 {{ head.orderQuantity }}</span>
      <span class="sep">|</span>
      <span>{{ head.partnerName }}</span>
    </div>
    <el-table :data="nodes" border v-loading="loading" max-height="420" :header-cell-style="{ background: '#f5f7fa', color: '#606266' }">
      <el-table-column type="index" label="#" width="45" align="center" />
      <el-table-column prop="nodeType" label="类型" width="100">
        <template slot-scope="scope">
          <el-tag :type="tagType(scope.row.nodeType)" size="mini">{{ typeLabel(scope.row.nodeType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="billCode" label="单号" min-width="140">
        <template slot-scope="scope">
          <a v-if="canOpen(scope.row)" class="code-link" @click="openDoc(scope.row)">{{ scope.row.billCode }}</a>
          <span v-else>{{ scope.row.billCode }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="billDate" label="日期" width="110" align="center">
        <template slot-scope="scope">{{ formatDate(scope.row.billDate) }}</template>
      </el-table-column>
      <el-table-column prop="approveStatus" label="状态" width="80" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.approveStatus === '2'" type="success" size="mini">已审</el-tag>
          <el-tag v-else-if="scope.row.nodeType === 'production'" size="mini">{{ prodStatus(scope.row.approveStatus) }}</el-tag>
          <el-tag v-else type="info" size="mini">未审</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="quantity" label="数量" width="90" align="right" />
      <el-table-column prop="amount" label="金额" width="100" align="right" />
      <el-table-column prop="extraInfo" label="备注" min-width="100" show-overflow-tooltip />
    </el-table>
    <div v-if="!loading && (!nodes || !nodes.length)" class="empty-tip">暂无下游单据</div>
    <span slot="footer"><el-button @click="visibleInner = false">关闭</el-button></span>
    <div v-show="false">
      <component :is="pageComp" v-if="pageComp" ref="page" />
    </div>
  </el-dialog>
</template>

<script>
import { docGet } from '../../api/erp/doc'
import { fetchPerms } from '../../api/auth'

const TYPE_LABELS = {
  shippingNotice: '发货通知', production: '生产工单', stockOut: '销售出库', return: '退货单', stockReturn: '退货入库',
  receivable: '应收单', invoice: '发票', receipt: '收款', returnSettle: '退货结算',
  invoiceRed: '销项红字', receiptRed: '收款红冲'
}

const DOCS = {
  shippingNotice: { perm: 'sale:salesShippingNotice:info', load: () => import('../../views/sale/salesShippingNotice.vue') },
  production: { perm: 'production:productionOrder:info', load: () => import('../../views/production/productionOrder.vue') },
  stockOut: { perm: 'sale:salesStockOut:info', load: () => import('../../views/sale/salesStockOut.vue') },
  return: { perm: 'sale:salesReturn:info', load: () => import('../../views/sale/salesReturn.vue') },
  stockReturn: { perm: 'sale:salesStockReturn:info', load: () => import('../../views/sale/salesStockReturn.vue') },
  receivable: { perm: 'finance:receivable:info', load: () => import('../../views/finance/Receivable.vue') },
  invoice: { perm: 'finance:invoiceSales:info', load: () => import('../../views/finance/InvoiceSales.vue') },
  receipt: { perm: 'finance:receipt:info', load: () => import('../../views/finance/Receipt.vue') },
  returnSettle: { perm: 'finance:salesReturnSettle:info', load: () => import('../../views/finance/SalesReturnSettle.vue') },
  invoiceRed: { perm: 'finance:invoiceSalesRed:info', load: () => import('../../views/finance/InvoiceSalesRed.vue') },
  receiptRed: { perm: 'finance:receiptRed:info', load: () => import('../../views/finance/ReceiptRed.vue') }
}

export default {
  props: {
    visible: { type: Boolean, default: false },
    orderLineId: { type: String, default: '' }
  },
  data() { return { visibleInner: false, loading: false, head: null, nodes: [], pageComp: null, permCodes: [] } },
  created() { fetchPerms().then(res => { this.permCodes = res.data || [] }).catch(() => {}) },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.visibleInner = !!val
        if (val && this.orderLineId) this.loadTrace()
      }
    },
    orderLineId(val) {
      if (this.visibleInner && val) this.loadTrace()
    }
  },
  methods: {
    isAuth(perm) {
      const user = this.$store.state.user || {}
      const codes = Array.isArray(user.roleCodes) ? user.roleCodes : []
      if (['TENANT_ADMIN', 'PLATFORM_ADMIN'].indexOf(String(user.roleCode || '')) >= 0 || codes.indexOf('TENANT_ADMIN') >= 0 || codes.indexOf('PLATFORM_ADMIN') >= 0) return true
      return this.permCodes.indexOf(perm) >= 0
    },
    loadTrace() {
      this.loading = true
      this.head = null
      this.nodes = []
      docGet('/sale/salesOrderExecution/trace/' + this.orderLineId).then(res => {
        this.head = res.data || null
        this.nodes = (res.data && res.data.nodes) || []
      }).catch(err => {
        this.head = null
        this.nodes = []
        this.$message.error((err && err.message) || '加载销售追溯失败')
      }).finally(() => { this.loading = false })
    },
    canOpen(row) {
      const cfg = DOCS[row.nodeType]
      return !!(row && row.billId && cfg && this.isAuth(cfg.perm))
    },
    openDoc(row) {
      const cfg = DOCS[row.nodeType]
      if (!row.billId || !cfg) return
      if (!this.isAuth(cfg.perm)) { this.$message.warning('无查看该单据的权限'); return }
      cfg.load().then(mod => {
        this.pageComp = mod.default
        this.$nextTick(() => {
          const page = this.$refs.page
          if (!page) return
          if (typeof page.open === 'function') page.open(row.billId, true)
          else if (typeof page.addOrUpdateHandle === 'function') page.addOrUpdateHandle(row.billId, true)
        })
      }).catch(() => this.$message.error('打开单据详情失败'))
    },
    typeLabel(t) { return TYPE_LABELS[t] || t },
    tagType(t) {
      if (t === 'shippingNotice') return 'warning'
      if (t === 'invoice' || t === 'receipt') return 'success'
      if (t === 'receivable') return ''
      return 'info'
    },
    prodStatus(s) {
      const map = { '0': '草稿', '1': '已下达', '3': '已完工', '4': '已关闭' }
      return map[s] || s
    },
    formatDate(d) { return d ? String(d).slice(0, 10) : '' },
    handleClose() { this.$emit('update:visible', false) }
  }
}
</script>

<style scoped>
.trace-head { margin-bottom: 12px; color: #606266; font-size: 13px; }
.trace-head .sep { margin: 0 8px; color: #dcdfe6; }
.empty-tip { text-align: center; color: #909399; padding: 24px 0; }
.code-link { color: #348adb; cursor: pointer; }
</style>
